import {
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import EvChargerSandIcon from "assets/ev-charger-color-sand.png";
import EvChargerSnowIcon from "assets/ev-charger-color-snow.png";
import EvChargerStoneIcon from "assets/ev-charger-color-stone.png";
import EvChargerVolcanicIcon from "assets/ev-charger-color-volcanic.png";
import { ReactComponent as EvChargerOn } from "assets/ev-charger-on.svg";
import EvChargerImageSand from "assets/ev-charger-sand.png";
import EvChargerImageSnow from "assets/ev-charger-snow.png";
import EvChargerImageStone from "assets/ev-charger-stone.png";
import EvChargerImageVolcanic from "assets/ev-charger-volcanic.png";
import { ReactComponent as ChargerIcon } from "assets/ev-type-2-charger.svg";
import unboundActions from "domain/core/actions";
import {
  getEvChargerInclusion,
  getSelectedEvChargerColor,
} from "domain/core/selectors";
import { EvChargerColors } from "domain/core/types";
import { useActions, useReduxState } from "re-reduced";
import React, { ChangeEvent, useCallback } from "react";
import { styled } from "styled-components";
import {
  CapitalisedTypography,
  Card,
  HeroTypography,
  RowLayout,
  RowLayoutLeft,
} from "ui/components/StyledComponents";
import theme, { COLORS, PADDING } from "ui/theme";

const SCard = styled(Card)`
  background-color: ${COLORS.backgroundIndigo};
  margin: ${PADDING.medium} 0;
  padding: 0;
`;

const CardContentContainer = styled.div`
  padding: 3rem;
`;

const SRowLayout = styled(RowLayout)`
  align-items: center;
`;

const SRowLayoutLeft = styled(RowLayoutLeft)`
  align-items: center;
  flex-wrap: wrap;

  & :first-child {
    margin-right: ${PADDING.small};
  }
`;

const TextContent = styled.div`
  @media (min-width: ${theme.breakpoints.values.sm}px) {
    padding: 0 3rem;
  }
`;

const SHeroTypography = styled(HeroTypography)`
  color: ${COLORS.orange};
  margin-top: 1rem;
`;

const SCollapse = styled(Collapse)`
  background: white;
`;

const CollapseContent = styled.div`
  padding: 0 3rem 3rem;
`;

const SRadioGroup = styled(RadioGroup)`
  & :first-child {
    margin-left: 0;
  }

  & :last-child {
    margin-right: 0;
  }
`;

const CheckedIcon = styled.img`
  outline: 4px solid ${COLORS.orange};
  outline-offset: -4px;
  border-radius: 50%;
`;

const ChargerImages = styled.div`
  position: relative;

  @media (max-width: ${theme.breakpoints.values.sm}px) {
    width: 100%;
  }
`;

const Charger = styled.img`
  position: relative;
`;

const ChargerSelected = styled(EvChargerOn)`
  position: absolute;
  top: 85px;
  left: 42px;
`;

const stateSelectorMap = {
  includeEvCharger: getEvChargerInclusion,
  selectedEvChargerColor: getSelectedEvChargerColor,
};

type EvChargerAddOnProps = {
  onEvChargerDetailsDialogOpen: () => void;
};

export const EvChargerAddOn: React.FC<EvChargerAddOnProps> = ({
  onEvChargerDetailsDialogOpen,
}) => {
  const reduxState = useReduxState(stateSelectorMap);
  const actions = useActions(unboundActions);

  const chargerIcons = {
    snow: EvChargerSnowIcon,
    sand: EvChargerSandIcon,
    stone: EvChargerStoneIcon,
    volcanic: EvChargerVolcanicIcon,
  };

  const chargerImages = {
    snow: EvChargerImageSnow,
    sand: EvChargerImageSand,
    stone: EvChargerImageStone,
    volcanic: EvChargerImageVolcanic,
  };

  const handleAddEvCharger = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      actions.setEvChargerInclusion(checked);
    },
    [reduxState.includeEvCharger],
  );

  const handleEvColorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as EvChargerColors;
      actions.setEvChargerColor(value);
    },
    [reduxState.selectedEvChargerColor],
  );

  return (
    <>
      <Typography variant="h2">Add a smart charger to your plan</Typography>
      <SCard>
        <CardContentContainer>
          <SRowLayout>
            <SRowLayoutLeft>
              <ChargerImages>
                <Charger
                  srcSet={
                    chargerImages[reduxState.selectedEvChargerColor || "snow"]
                  }
                  src={
                    chargerImages[reduxState.selectedEvChargerColor || "snow"]
                  }
                  alt="Evnex smart charger in snow color"
                />
                {reduxState.includeEvCharger && (
                  <ChargerSelected>
                    <EvChargerOn />
                  </ChargerSelected>
                )}
              </ChargerImages>
              <TextContent>
                <Typography variant="h3">Add Evnex smart charger</Typography>
                <SRowLayoutLeft>
                  <ChargerIcon />
                  <Typography variant="body2">
                    Type 2 charger | 7.4 kW
                  </Typography>
                </SRowLayoutLeft>
                <Typography variant="body1">
                  Pay now, or 24 months interest free repayments.
                  <br />
                  Simple install from $2,195.00 - T&C’s apply.*
                </Typography>
                <Link
                  component="button"
                  id="button-switch-plan"
                  onClick={onEvChargerDetailsDialogOpen}
                >
                  *Smart charger details and eligibility
                </Link>
              </TextContent>
            </SRowLayoutLeft>
            <Switch
              checked={reduxState.includeEvCharger}
              onChange={handleAddEvCharger}
              inputProps={{ "aria-label": "Add a smart charger to your plan?" }}
              color="secondary"
            />
          </SRowLayout>
        </CardContentContainer>
        <SCollapse in={reduxState.includeEvCharger}>
          <CollapseContent>
            <SHeroTypography>
              Great, you’ve added a smart charger
            </SHeroTypography>
            <FormControl>
              <FormLabel id="charger-color-radio-buttons">
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  id="text-color-choice"
                >
                  Please select your choice of colour
                </Typography>
              </FormLabel>
              <SRadioGroup
                row
                aria-labelledby="charger-color-radio-buttons"
                name="charger-colors"
                onChange={handleEvColorChange}
                defaultValue={reduxState.selectedEvChargerColor}
              >
                {Object.values(EvChargerColors).map((color) => {
                  return (
                    <FormControlLabel
                      value={color}
                      control={
                        <Radio
                          color="secondary"
                          checkedIcon={
                            <CheckedIcon
                              srcSet={chargerIcons[color]}
                              src={chargerIcons[color]}
                              alt={`Ev charger ${color} color`}
                            />
                          }
                          icon={
                            <img
                              srcSet={chargerIcons[color]}
                              src={chargerIcons[color]}
                              alt={`Ev charger ${color} color`}
                            />
                          }
                        />
                      }
                      label={
                        <CapitalisedTypography variant="subtitle2">
                          {color}
                        </CapitalisedTypography>
                      }
                      labelPlacement="bottom"
                    />
                  );
                })}
              </SRadioGroup>
            </FormControl>
          </CollapseContent>
        </SCollapse>
      </SCard>
    </>
  );
};
